import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import hrService from '../services/hrService';
import useUIStore from './uiStore';

const useHRStore = create()(
  devtools(
    (set, get) => ({
      // Employee Management State
      employees: [],
      currentEmployee: null,
      totalEmployees: 0,
      
      // Leave Management State
      leaves: [],
      leaveBalances: [],
      currentLeave: null,
      totalLeaves: 0,
      
      // Attendance State
      attendanceRecords: [],
      todayAttendance: null,
      attendanceSummary: null,
      todayOverview: null,
      totalAttendance: 0,
      
      // Performance Management State
      performanceReviews: [],
      goals: [],
      currentReview: null,
      currentGoal: null,
      totalReviews: 0,
      totalGoals: 0,
      
      // Recruitment State
      jobPostings: [],
      candidates: [],
      currentJob: null,
      currentCandidate: null,
      totalJobs: 0,
      totalCandidates: 0,
      
      // Organization Chart State
      orgChart: null,
      departments: [],
      positions: [],
      
      // UI State
      isLoading: false,
      criticalError: null,        // Only for employee/HR data loading failures
      operationError: null,       // For non-critical operations
      filters: {},
      meta: null,
      pagination: {
        page: 1,
        limit: 20,
        total: 0
      },
      
      // Generic Actions
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ operationError: error }),
      clearError: () => set({ criticalError: null, operationError: null }),
      
      // Employee Actions
      fetchEmployees: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          // Get current state to check filters
          const currentState = get();
          console.log('🔄 fetchEmployees called with params:', params);
          console.log('📌 Current filters in store:', currentState.filters);
          console.log('📌 Combined query params:', { ...currentState.filters, ...params });
          
          const response = await hrService.getEmployees(params);
          console.log('📥 Raw API response:', response);
          console.log('📊 Response data structure:', {
            status: response.status,
            data: response.data,
            meta: response.meta,
            employees: response.data?.employees,
            firstEmployee: response.data?.employees?.[0]
          });
          
          const employees = response.data?.employees || [];
          console.log('👥 Extracted employees:', employees);
          console.log('👤 First employee data:', employees[0]);
          
          set({
            employees,
            totalEmployees: response.meta?.total || 0,
            meta: response.meta || null,
            pagination: {
              ...get().pagination,
              page: response.meta?.page || 1,
              limit: response.meta?.limit || 20,
              total: response.meta?.total || 0
            },
            isLoading: false
          });
          return response.data;
        } catch (error) {
          console.error('🚨 ~ fetchEmployees error:', error);
          set({ 
            criticalError: error.message, 
            employees: [],
            totalEmployees: 0,
            isLoading: false 
          });
          // Don't throw error - return empty data
          return { employees: [], total: 0 };
        }
      },
      
      fetchEmployee: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getEmployee(id);
          set({ 
            currentEmployee: response.data?.employee || response.data, 
            isLoading: false 
          });
          return response.data?.employee || response.data;
        } catch (error) {
          set({ criticalError: error.message, isLoading: false });
          throw error;
        }
      },
      
      createEmployee: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.createEmployee(data);
          const employee = response.data?.employee || response.data;
          set(state => ({
            employees: [employee, ...state.employees],
            totalEmployees: state.totalEmployees + 1,
            currentEmployee: employee,
            isLoading: false
          }));
          
          // Success notification
          useUIStore.getState().addNotification({
            message: 'Employee created successfully',
            type: 'success'
          });
          
          return employee;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          
          // Error notification
          useUIStore.getState().addNotification({
            message: error.message || 'Failed to create employee',
            type: 'error'
          });
          
          throw error;
        }
      },
      
      updateEmployee: async (id, data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.updateEmployee(id, data);
          const employee = response.data?.employee || response.data;
          set(state => ({
            employees: state.employees.map(emp => 
              emp._id === id ? employee : emp
            ),
            currentEmployee: employee,
            isLoading: false
          }));
          return employee;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          
          // Error notification
          useUIStore.getState().addNotification({
            message: error.message || 'Failed to update employee',
            type: 'error'
          });
          
          throw error;
        }
      },
      
      deleteEmployee: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          await hrService.deleteEmployee(id);
          set(state => ({
            employees: state.employees.filter(emp => emp._id !== id),
            totalEmployees: state.totalEmployees - 1,
            currentEmployee: state.currentEmployee?._id === id ? null : state.currentEmployee,
            isLoading: false
          }));
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          
          // Error notification
          useUIStore.getState().addNotification({
            message: error.message || 'Failed to delete employee',
            type: 'error'
          });
          
          throw error;
        }
      },
      
      // Leave Management Actions
      fetchLeaves: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getLeaves(params);
          console.log('Fetch leaves response:', response);
          
          // The backend returns { status: 'success', data: { leaves: { docs, totalDocs, ... } } }
          const leavesData = response.data?.leaves || response.data || {};
          const leaves = leavesData.docs || leavesData.leaves || [];
          
          set({
            leaves,
            totalLeaves: leavesData.totalDocs || leavesData.total || leaves.length,
            meta: {
              page: leavesData.page || 1,
              limit: leavesData.limit || 20,
              totalPages: leavesData.totalPages || 1,
              hasNextPage: leavesData.hasNextPage || false,
              hasPrevPage: leavesData.hasPrevPage || false
            },
            pagination: {
              ...get().pagination,
              page: leavesData.page || 1,
              limit: leavesData.limit || 20,
              total: leavesData.totalDocs || leaves.length
            },
            isLoading: false
          });
          return response.data;
        } catch (error) {
          set({ 
            criticalError: error.message,
            leaves: [],
            totalLeaves: 0,
            isLoading: false 
          });
          return { leaves: [], total: 0 };
        }
      },
      
      fetchLeave: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getLeave(id);
          const leave = response.data?.leave || response.data;
          set({ currentLeave: leave, isLoading: false });
          return leave;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      createLeaveRequest: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.createLeaveRequest(data);
          const leave = response.data?.leave || response.data;
          set(state => ({
            leaves: [leave, ...state.leaves],
            totalLeaves: state.totalLeaves + 1,
            currentLeave: leave,
            isLoading: false
          }));
          return leave;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      submitLeave: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.submitLeave(id);
          const leave = response.data?.leave || response.data;
          set(state => ({
            leaves: state.leaves.map(l => l._id === id ? leave : l),
            currentLeave: state.currentLeave?._id === id ? leave : state.currentLeave,
            isLoading: false
          }));
          return leave;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      approveLeave: async (id, data = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.approveLeave(id, data);
          const leave = response.data?.leave || response.data;
          set(state => ({
            leaves: state.leaves.map(l => l._id === id ? leave : l),
            currentLeave: state.currentLeave?._id === id ? leave : state.currentLeave,
            isLoading: false
          }));
          return leave;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      rejectLeave: async (id, data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.rejectLeave(id, data);
          const leave = response.data?.leave || response.data;
          set(state => ({
            leaves: state.leaves.map(l => l._id === id ? leave : l),
            currentLeave: state.currentLeave?._id === id ? leave : state.currentLeave,
            isLoading: false
          }));
          return leave;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      cancelLeave: async (id, data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.cancelLeave(id, data);
          const leave = response.data?.leave || response.data;
          set(state => ({
            leaves: state.leaves.map(l => l._id === id ? leave : l),
            currentLeave: state.currentLeave?._id === id ? leave : state.currentLeave,
            isLoading: false
          }));
          return leave;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      // Leave Balance Actions
      fetchLeaveBalance: async (employeeId) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getLeaveBalance(employeeId);
          const balance = response.data?.balance || response.data;
          
          // Only update if we got valid balance data
          if (balance) {
            set(state => ({
              leaveBalances: state.leaveBalances.map(b => 
                b.employee === employeeId ? balance : b
              ).concat(
                state.leaveBalances.find(b => b.employee === employeeId) ? [] : [balance]
              ),
              isLoading: false
            }));
            
            // Update currentEmployee's leaveBalance if it matches
            if (get().currentEmployee?._id === employeeId) {
              set(state => ({
                currentEmployee: {
                  ...state.currentEmployee,
                  leaveBalance: balance
                }
              }));
            }
          } else {
            set({ isLoading: false });
          }
          
          return balance;
        } catch (error) {
          // Handle 404 gracefully - just set loading to false, don't set error
          if (error.response?.status === 404 || error.message?.includes('404')) {
            set({ isLoading: false });
            return null;
          }
          
          // For other errors, set the error state but don't throw
          set({ operationError: error.message, isLoading: false });
          return null;
        }
      },
      
      // Attendance Actions
      fetchAttendance: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          console.log('Fetching attendance with params:', params);
          const response = await hrService.getAttendance(params);
          console.log('Attendance API response:', response);
          
          // Handle paginated response from backend
          const attendanceData = response.data?.docs || response.data?.records || response.data || [];
          const totalRecords = response.data?.totalDocs || response.data?.total || attendanceData.length;
          
          console.log('Extracted attendance data:', attendanceData);
          console.log('Total records:', totalRecords);
          
          set(state => ({
            attendanceRecords: attendanceData,
            totalAttendance: totalRecords,
            meta: {
              page: response.data?.page || 1,
              limit: response.data?.limit || 20,
              totalPages: response.data?.totalPages || 1,
              hasNextPage: response.data?.hasNextPage || false,
              hasPrevPage: response.data?.hasPrevPage || false
            },
            attendancePagination: {
              ...state.attendancePagination,
              page: response.data?.page || 1,
              limit: response.data?.limit || 20,
              total: totalRecords
            },
            isLoading: false
          }));
          
          // Return the full response for EntityListContainer
          return {
            data: attendanceData,
            meta: {
              total: totalRecords,
              totalPages: response.data?.totalPages || 1,
              page: response.data?.page || 1,
              limit: response.data?.limit || 20
            }
          };
        } catch (error) {
          console.error('Error fetching attendance:', error);
          set({ 
            criticalError: error.message,
            attendanceRecords: [],
            totalAttendance: 0,
            isLoading: false 
          });
          return { data: [], meta: { total: 0 } };
        }
      },
      
      getTodayAttendance: async () => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getCurrentAttendanceStatus();
          const attendance = response.data?.attendance;
          set({ todayAttendance: attendance, isLoading: false });
          return attendance;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          // Don't throw error, just return null
          return null;
        }
      },
      
      clockIn: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.clockIn(data);
          const attendance = response.data?.attendance;
          set({ todayAttendance: attendance, isLoading: false });
          return attendance;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      clockOut: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.clockOut(data);
          const attendance = response.data?.attendance;
          set({ todayAttendance: attendance, isLoading: false });
          return attendance;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      startBreak: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.startBreak(data);
          const attendance = response.data?.attendance;
          set({ todayAttendance: attendance, isLoading: false });
          return attendance;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      endBreak: async () => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.endBreak();
          const attendance = response.data?.attendance;
          set({ todayAttendance: attendance, isLoading: false });
          return attendance;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      // My Attendance Actions (Employee self-service)
      getMyAttendance: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getMyAttendance(params);
          // Handle the response structure from backend
          const records = Array.isArray(response.data) ? response.data : 
                         response.data?.records || response.records || [];
          const total = response.data?.total || response.total || records.length;
          
          set({
            attendanceRecords: records,
            totalAttendance: total,
            meta: response.meta || null,
            isLoading: false
          });
          return { records, total };
        } catch (error) {
          set({ 
            criticalError: error.message,
            attendanceRecords: [],
            totalAttendance: 0,
            isLoading: false 
          });
          return { records: [], total: 0 };
        }
      },
      
      getMyAttendanceSummary: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getMyAttendanceSummary(params);
          // The backend returns the summary directly in response.data
          const summary = response.data || response || null;
          set({ 
            attendanceSummary: summary, 
            isLoading: false 
          });
          return summary;
        } catch (error) {
          set({ 
            criticalError: error.message, 
            attendanceSummary: null,
            isLoading: false 
          });
          return null;
        }
      },
      
      // Get today's attendance overview
      getTodayOverview: async () => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getTodayOverview();
          const overview = response.data || null;
          set({ 
            todayOverview: overview, 
            isLoading: false 
          });
          return overview;
        } catch (error) {
          set({ 
            criticalError: error.message, 
            todayOverview: null,
            isLoading: false 
          });
          return null;
        }
      },
      
      // Performance Management Actions
      fetchPerformanceReviews: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getPerformanceReviews(params);
          // Ensure we always get an array
          const reviews = Array.isArray(response.data?.reviews) ? response.data.reviews : 
                         Array.isArray(response.data) ? response.data : [];
          set({
            performanceReviews: reviews,
            totalReviews: response.meta?.total || 0,
            meta: response.meta || null,
            isLoading: false
          });
          return response.data;
        } catch (error) {
          set({ 
            criticalError: error.message,
            performanceReviews: [],
            totalReviews: 0,
            isLoading: false 
          });
          return { reviews: [], total: 0 };
        }
      },
      
      createPerformanceReview: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.createPerformanceReview(data);
          const review = response.data?.review || response.data;
          set(state => ({
            performanceReviews: [review, ...state.performanceReviews],
            totalReviews: state.totalReviews + 1,
            currentReview: review,
            isLoading: false
          }));
          return review;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      updatePerformanceReview: async (id, data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.updatePerformanceReview(id, data);
          const review = response.data?.review || response.data;
          set(state => ({
            performanceReviews: state.performanceReviews.map(r => 
              r._id === id ? review : r
            ),
            currentReview: state.currentReview?._id === id ? review : state.currentReview,
            isLoading: false
          }));
          return review;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      deletePerformanceReview: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          await hrService.deletePerformanceReview(id);
          set(state => ({
            performanceReviews: state.performanceReviews.filter(r => r._id !== id),
            totalReviews: state.totalReviews - 1,
            currentReview: state.currentReview?._id === id ? null : state.currentReview,
            isLoading: false
          }));
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      // Goals Actions
      fetchGoals: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getGoals(params);
          // Ensure we always get an array
          const goals = Array.isArray(response.data?.goals) ? response.data.goals : 
                       Array.isArray(response.data) ? response.data : [];
          set({
            goals,
            totalGoals: response.meta?.total || goals.length,
            meta: response.meta || null,
            isLoading: false
          });
          return response.data;
        } catch (error) {
          set({ 
            criticalError: error.message,
            goals: [],
            totalGoals: 0,
            isLoading: false 
          });
          return { goals: [], total: 0 };
        }
      },
      
      deleteGoal: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          await hrService.updateGoal(id, { status: 'deleted' });
          set(state => ({
            goals: state.goals.filter(g => g._id !== id),
            totalGoals: state.totalGoals - 1,
            currentGoal: state.currentGoal?._id === id ? null : state.currentGoal,
            isLoading: false
          }));
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      createGoal: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.createGoal(data);
          const goal = response.data?.goal || response.data;
          set(state => ({
            goals: [goal, ...state.goals],
            totalGoals: state.totalGoals + 1,
            currentGoal: goal,
            isLoading: false
          }));
          return goal;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      updateGoal: async (id, data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.updateGoal(id, data);
          const goal = response.data?.goal || response.data;
          set(state => ({
            goals: state.goals.map(g => g._id === id ? goal : g),
            currentGoal: state.currentGoal?._id === id ? goal : state.currentGoal,
            isLoading: false
          }));
          return goal;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      updateGoalProgress: async (id, data) => {
        return get().updateGoal(id, data);
      },
      
      // Recruitment Actions
      fetchJobPostings: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getJobPostings(params);
          // Ensure we always get an array
          const jobs = Array.isArray(response.data?.jobs) ? response.data.jobs : 
                      Array.isArray(response.data) ? response.data : [];
          set({
            jobPostings: jobs,
            totalJobs: response.meta?.total || 0,
            meta: response.meta || null,
            isLoading: false
          });
          return response.data;
        } catch (error) {
          set({ 
            criticalError: error.message,
            jobPostings: [],
            totalJobs: 0,
            isLoading: false 
          });
          return { jobs: [], total: 0 };
        }
      },
      
      createJobPosting: async (data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.createJob(data);
          const job = response.data?.job || response.data;
          set(state => ({
            jobPostings: [job, ...state.jobPostings],
            totalJobs: state.totalJobs + 1,
            currentJob: job,
            isLoading: false
          }));
          return job;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      updateJobPosting: async (id, data) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.updateJob(id, data);
          const job = response.data?.job || response.data;
          set(state => ({
            jobPostings: state.jobPostings.map(j => 
              j._id === id ? job : j
            ),
            currentJob: state.currentJob?._id === id ? job : state.currentJob,
            isLoading: false
          }));
          return job;
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      deleteJobPosting: async (id) => {
        set({ isLoading: true, criticalError: null });
        try {
          await hrService.deleteJob(id);
          set(state => ({
            jobPostings: state.jobPostings.filter(j => j._id !== id),
            totalJobs: state.totalJobs - 1,
            currentJob: state.currentJob?._id === id ? null : state.currentJob,
            isLoading: false
          }));
        } catch (error) {
          set({ operationError: error.message, isLoading: false });
          throw error;
        }
      },
      
      fetchCandidates: async (params = {}) => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getCandidates(params);
          // Ensure we always get an array
          const candidates = Array.isArray(response.data?.candidates) ? response.data.candidates : 
                            Array.isArray(response.data) ? response.data : [];
          set({
            candidates,
            totalCandidates: response.meta?.total || 0,
            meta: response.meta || null,
            isLoading: false
          });
          return response.data;
        } catch (error) {
          set({ 
            criticalError: error.message,
            candidates: [],
            totalCandidates: 0,
            isLoading: false 
          });
          return { candidates: [], total: 0 };
        }
      },
      
      // Organization Chart Actions
      fetchOrgChart: async () => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getOrgChart();
          const orgChart = response.data;
          set({ orgChart, isLoading: false });
          return orgChart;
        } catch (error) {
          set({ criticalError: error.message, isLoading: false, orgChart: null });
          throw error;
        }
      },
      
      fetchDepartments: async () => {
        set({ isLoading: true, criticalError: null });
        try {
          const response = await hrService.getDepartments();
          const departments = response.data?.departments || response.data || [];
          set({ departments, isLoading: false });
          return departments;
        } catch (error) {
          set({ 
            criticalError: error.message, 
            departments: [],
            isLoading: false 
          });
          return [];
        }
      },
      
      // Attendance-specific filter and pagination actions
      updateAttendanceFilters: (newFilters) => {
        const currentState = get();
        
        let updatedFilters = { ...currentState.attendanceFilters };
        
        // Handle search term separately
        if ('search' in newFilters) {
          if (newFilters.search) {
            updatedFilters.search = newFilters.search;
          } else {
            delete updatedFilters.search;
          }
        }
        
        // Handle other filters
        Object.entries(newFilters).forEach(([key, value]) => {
          if (key === 'search') return; // Already handled above
          
          if (value === 'all' || value === '' || value === null || value === undefined) {
            delete updatedFilters[key];
          } else {
            updatedFilters[key] = value;
          }
        });
        
        if (Object.keys(newFilters).length === 0) {
          updatedFilters = {};
        }
        
        set(state => ({
          attendanceFilters: updatedFilters,
          attendancePagination: { ...(state.attendancePagination || { limit: 20 }), page: 1 }
        }));
        
        // Refetch attendance data with updated filters
        const pagination = currentState.attendancePagination || { limit: 20 };
        if (currentState.fetchAttendance && typeof currentState.fetchAttendance === 'function') {
          currentState.fetchAttendance({
            ...updatedFilters,
            page: 1,
            limit: pagination.limit || 20
          });
        }
      },
      
      updateAttendancePagination: (newPagination) => {
        set(state => ({
          attendancePagination: { ...state.attendancePagination, ...newPagination }
        }));
        
        const currentState = get();
        currentState.fetchAttendance({
          ...currentState.attendanceFilters,
          ...newPagination,
          limit: newPagination.limit || currentState.attendancePagination.limit
        });
      },
      
      // Filter and Pagination Actions
      updateFilters: (newFilters) => {
        const currentState = get();
        
        // Start with existing filters
        let updatedFilters = { ...currentState.filters };
        
        // Process each new filter
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === 'all' || value === '' || value === null || value === undefined) {
            // Remove the filter if it's set to 'all' or empty
            delete updatedFilters[key];
          } else {
            // Otherwise, update the filter value
            updatedFilters[key] = value;
          }
        });
        
        // If empty object passed, clear all filters
        if (Object.keys(newFilters).length === 0) {
          updatedFilters = {};
        }
        
        set(state => ({
          filters: updatedFilters,
          pagination: { ...state.pagination, page: 1 } // Reset to first page when filters change
        }));
        
        // Automatically refetch data with updated filters
        currentState.fetchEmployees({
          ...updatedFilters,
          page: 1,
          limit: currentState.pagination.limit
        });
      },
      
      updatePagination: (newPagination) => {
        set(state => ({
          pagination: { ...state.pagination, ...newPagination }
        }));
        // Automatically refetch data with new pagination
        const currentState = get();
        currentState.fetchEmployees({
          ...currentState.filters,
          ...newPagination,
          limit: newPagination.limit || currentState.pagination.limit
        });
      },
      
      clearFilters: () => {
        set({ filters: {}, pagination: { page: 1, limit: 20, total: 0 } });
        // Refetch data without filters
        get().fetchEmployees({ page: 1, limit: 20 });
      },
      
      // Utility Actions
      clearCurrentEmployee: () => set({ currentEmployee: null }),
      clearCurrentLeave: () => set({ currentLeave: null }),
      clearCurrentReview: () => set({ currentReview: null }),
      clearCurrentGoal: () => set({ currentGoal: null }),
      clearCurrentJob: () => set({ currentJob: null }),
      clearCurrentCandidate: () => set({ currentCandidate: null }),
      
      resetStore: () => set({
        employees: [],
        currentEmployee: null,
        totalEmployees: 0,
        leaves: [],
        leaveBalances: [],
        currentLeave: null,
        totalLeaves: 0,
        attendanceRecords: [],
        todayAttendance: null,
        attendanceSummary: null,
        todayOverview: null,
        totalAttendance: 0,
        performanceReviews: [],
        goals: [],
        currentReview: null,
        currentGoal: null,
        totalReviews: 0,
        totalGoals: 0,
        jobPostings: [],
        candidates: [],
        currentJob: null,
        currentCandidate: null,
        totalJobs: 0,
        totalCandidates: 0,
        orgChart: null,
        departments: [],
        positions: [],
        isLoading: false,
        criticalError: null,
        operationError: null,
        filters: {},
        attendanceFilters: {}, // Separate filters for attendance
        meta: null,
        pagination: {
          page: 1,
          limit: 20,
          total: 0
        },
        attendancePagination: { // Separate pagination for attendance
          page: 1,
          limit: 20,
          total: 0
        }
      })
    }),
    {
      name: 'hr-store'
    }
  )
);

export default useHRStore;